import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { 
  Lock,
  Menu,
  X,
  Phone,
  Mail,
  MapPin,
  Facebook,
  Instagram,
  Youtube,
  ChevronDown,
  Sparkles,
  Car,
  ShoppingCart,
  Wrench,
  Sun,
  Moon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/contexts/ThemeContext';

const LandingVanguard = () => {
  const { theme, toggleTheme } = useTheme();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [hoveredBrand, setHoveredBrand] = useState(null);
  const containerRef = useRef(null);

  // Slides con las imágenes proporcionadas y logos de marcas
  const slides = [
    {
      image: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/mjxopt6i_Fondo_BYD_Seal.png',
      title: 'BYD SEAL',
      subtitle: 'Futuro Eléctrico',
      description: '100% Eléctrico | 520km Autonomía | Carga Rápida',
      brand: 'BYD',
      brandLogo: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/rqt16dv8_Logo_BYD_Blanco_para_fondo_oscuro.png',
      brandLogoSize: 'small', // BYD logo más pequeño
      color: '#00d4ff'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/a8a9t39m_Fondo_Geely_Coolray.png',
      title: 'GEELY COOLRAY',
      subtitle: 'Diseño Audaz',
      description: 'SUV Compacta | Tecnología Avanzada | Máximo Confort',
      brand: 'GEELY',
      brandLogo: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/925mkif1_Logo_Geely_Blanco_para_fondo_oscuro.png',
      color: '#7aa8b7'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/ispcg3ql_Fondo_Geely_X3.png',
      title: 'GEELY X3',
      subtitle: 'Versatilidad Total',
      description: 'Espacioso | Eficiente | Conectividad Premium',
      brand: 'GEELY',
      brandLogo: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/925mkif1_Logo_Geely_Blanco_para_fondo_oscuro.png',
      color: '#e74c3c'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/6epg1ed8_Fondo_Peugeot208.png',
      title: 'PEUGEOT 208',
      subtitle: 'Elegancia Urbana',
      description: 'Diseño Francés | i-Cockpit 3D | Performance Ágil',
      brand: 'PEUGEOT',
      brandLogo: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/usltsakg_Logo_Peugeot_Blanco_para_fondo_oscuro.png',
      color: '#1dd1a1'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/0v61ajlr_Fondo_Peugeot408.png',
      title: 'PEUGEOT 408',
      subtitle: 'Innovación Premium',
      description: 'Fastback | Tecnología Híbrida | Máximo Confort',
      brand: 'PEUGEOT',
      brandLogo: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/usltsakg_Logo_Peugeot_Blanco_para_fondo_oscuro.png',
      color: '#48dbfb'
    }
  ];

  // Cursor removed per user request

  // Auto advance con transición mejorada
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setHoveredBrand(null);
    };

    if (hoveredBrand) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [hoveredBrand]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div ref={containerRef} className="relative bg-background overflow-hidden transition-colors duration-500">
      {/* Cursor removed */}

      {/* Navbar */}
      <motion.nav 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5, type: "spring" }}
        className="fixed top-0 left-0 right-0 z-40 backdrop-blur-2xl border-b transition-colors duration-500"
        style={{
          background: theme === 'dark' 
            ? 'rgba(0, 0, 0, 0.2)' 
            : 'rgba(255, 255, 255, 0.85)',
          borderColor: theme === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)' 
            : 'rgba(0, 0, 0, 0.1)'
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <motion.div 
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              <Link to="/" className="flex items-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/7q1rfiej_Logo_Amaya_Blanco_para_fondo_oscuro.png"
                  alt="Amaya Motors"
                  className="h-10 w-auto"
                />
              </Link>
            </motion.div>

            {/* Desktop Menu con efectos premium ultra-refinados */}
            <div className="hidden md:flex items-center gap-1">
              {[
                { label: 'Marcas Oficiales', href: '#marcas' },
                { label: 'Vehículos 0km', href: '#recomendados' },
                { label: 'Usados Seleccionados', href: '#recomendados' },
                { label: 'Agenda Service', href: '#service' },
                { label: 'Taller', href: '#taller' },
                { label: 'Sucursales', href: '#sucursales' },
                { label: 'Contacto', href: '#contacto' }
              ].map((item, index) => (
                <div key={item.label} className="flex items-center">
                  <motion.a 
                    href={item.href}
                    className="relative px-4 py-2 text-sm font-medium group overflow-hidden"
                    style={{
                      color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.8)'
                    }}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index, type: "spring" }}
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="relative z-10 tracking-wide">{item.label}</span>
                    
                    {/* Efecto underline animado con gradiente dinámico */}
                    <motion.div 
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-cyan-400 to-blue-500"
                      initial={{ scaleX: 0, originX: 0 }}
                      whileHover={{ scaleX: 1 }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                    />
                    
                    {/* Efecto shimmer sutil */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full"
                      initial={{ x: "-100%" }}
                      whileHover={{ x: "100%" }}
                      transition={{ duration: 0.8, ease: "easeInOut" }}
                    />
                    
                    {/* Efecto glow en hover mejorado */}
                    <motion.div
                      className="absolute inset-0 rounded-lg bg-gradient-to-b from-white/0 to-white/0 group-hover:from-white/5 group-hover:to-transparent"
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                    
                    {/* Bordes luminosos sutiles */}
                    <motion.div
                      className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/0 to-transparent group-hover:via-primary/30"
                      transition={{ duration: 0.3 }}
                    />
                  </motion.a>
                  
                  {/* Separador vertical animado y elegante */}
                  {index < 6 && (
                    <motion.div 
                      className="h-5 w-px bg-gradient-to-b from-transparent via-white/20 to-transparent mx-1"
                      initial={{ scaleY: 0, opacity: 0 }}
                      animate={{ scaleY: 1, opacity: 0.5 }}
                      transition={{ 
                        duration: 0.5, 
                        delay: 0.1 * index + 0.3,
                        type: "spring",
                        stiffness: 100
                      }}
                      whileHover={{ scaleY: 1.3, opacity: 0.8 }}
                    />
                  )}
                </div>
              ))}
              
              {/* Botón Theme Toggle ultra-refinado */}
              <motion.button
                onClick={toggleTheme}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.1, type: "spring" }}
                whileHover={{ scale: 1.15 }}
                whileTap={{ scale: 0.9 }}
                className="relative p-2.5 transition-colors duration-300 group"
                style={{
                  color: theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.7)'
                }}
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? (
                  <Sun className="h-4 w-4 relative z-10 transition-transform duration-300 group-hover:rotate-12" />
                ) : (
                  <Moon className="h-4 w-4 relative z-10 transition-transform duration-300 group-hover:rotate-12" />
                )}
                
                {/* Círculo de fondo con glassmorphism */}
                <motion.div
                  className="absolute inset-0 rounded-full bg-white/5 backdrop-blur-sm"
                  initial={{ scale: 0.8, opacity: 0 }}
                  whileHover={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />
                
                {/* Anillo pulsante en hover */}
                <motion.div
                  className="absolute inset-0 rounded-full border border-primary/0 group-hover:border-primary/40"
                  initial={{ scale: 0.8 }}
                  whileHover={{ scale: 1.3, opacity: 0 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                />
                
                {/* Glow sutil */}
                <motion.div
                  className="absolute inset-0 rounded-full bg-primary/0 blur-md group-hover:bg-primary/20"
                  transition={{ duration: 0.3 }}
                />
              </motion.button>
              
              {/* Separador especial premium antes del botón staff */}
              <motion.div 
                className="mx-3 h-6 w-px bg-gradient-to-b from-transparent via-primary/40 to-transparent relative"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-400/60 to-transparent"
                  animate={{ 
                    opacity: [0.3, 0.7, 0.3],
                    scaleY: [0.8, 1, 0.8]
                  }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                />
              </motion.div>
              
              {/* Botón staff ultra-refinado y discreto */}
              <Link to="/login">
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.2, type: "spring" }}
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.9 }}
                  className="relative p-2.5 transition-colors duration-300 group"
                  style={{
                    color: theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.7)'
                  }}
                >
                  <Lock className="h-4 w-4 relative z-10 transition-transform duration-300 group-hover:rotate-12" />
                  
                  {/* Círculo de fondo con glassmorphism */}
                  <motion.div
                    className="absolute inset-0 rounded-full bg-white/5 backdrop-blur-sm"
                    initial={{ scale: 0.8, opacity: 0 }}
                    whileHover={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  />
                  
                  {/* Anillo pulsante en hover */}
                  <motion.div
                    className="absolute inset-0 rounded-full border border-primary/0 group-hover:border-primary/40"
                    initial={{ scale: 0.8 }}
                    whileHover={{ scale: 1.3, opacity: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                  
                  {/* Glow sutil */}
                  <motion.div
                    className="absolute inset-0 rounded-full bg-primary/0 blur-md group-hover:bg-primary/20"
                    transition={{ duration: 0.3 }}
                  />
                </motion.button>
              </Link>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2"
              style={{ color: theme === 'dark' ? 'white' : 'black' }}
            >
              <AnimatePresence mode="wait">
                {mobileMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                  >
                    <X className="h-6 w-6" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                  >
                    <Menu className="h-6 w-6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="md:hidden backdrop-blur-xl border-t overflow-hidden"
              style={{
                background: theme === 'dark' 
                  ? 'rgba(0, 0, 0, 0.95)' 
                  : 'rgba(255, 255, 255, 0.95)',
                borderColor: theme === 'dark' 
                  ? 'rgba(255, 255, 255, 0.05)' 
                  : 'rgba(0, 0, 0, 0.1)'
              }}
            >
              <div className="px-6 py-6 space-y-2">
                {[
                  { label: 'Marcas Oficiales', href: '#marcas' },
                  { label: 'Vehículos 0km', href: '#recomendados' },
                  { label: 'Usados Seleccionados', href: '#recomendados' },
                  { label: 'Agenda Service', href: '#service' },
                  { label: 'Taller', href: '#taller' },
                  { label: 'Sucursales', href: '#sucursales' },
                  { label: 'Contacto', href: '#contacto' }
                ].map((item, index) => (
                  <motion.a
                    key={item.label}
                    href={item.href}
                    initial={{ x: -50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: -50, opacity: 0 }}
                    transition={{ 
                      delay: index * 0.05,
                      type: "spring",
                      stiffness: 100,
                      damping: 15
                    }}
                    whileTap={{ scale: 0.95 }}
                    className="block py-3 px-4 rounded-lg transition-all duration-200 border border-transparent"
                    style={{
                      color: theme === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
                      backgroundColor: 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme === 'dark' 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(0, 0, 0, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="text-base font-medium">{item.label}</span>
                  </motion.a>
                ))}
                
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="pt-4 mt-4 border-t space-y-2"
                  style={{
                    borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <motion.button
                    onClick={toggleTheme}
                    whileTap={{ scale: 0.95 }}
                    className="w-full flex items-center gap-3 py-3 px-4 rounded-lg transition-all duration-200"
                    style={{
                      color: theme === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme === 'dark' 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(0, 0, 0, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {theme === 'dark' ? (
                      <>
                        <Sun className="h-5 w-5" />
                        <span className="text-base font-medium">Modo Claro</span>
                      </>
                    ) : (
                      <>
                        <Moon className="h-5 w-5" />
                        <span className="text-base font-medium">Modo Oscuro</span>
                      </>
                    )}
                  </motion.button>
                  
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <motion.div
                      whileTap={{ scale: 0.95 }}
                      className="flex items-center gap-3 py-3 px-4 rounded-lg transition-all duration-200"
                      style={{
                        color: theme === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <Lock className="h-5 w-5" />
                      <span className="text-base font-medium">Acceso Staff</span>
                    </motion.div>
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Hero Section con Slider VANGUARDISTA */}
      <section className="relative h-screen overflow-hidden">
        <AnimatePresence mode="sync">
          <motion.div
            key={currentSlide}
            className="absolute inset-0"
          >
            {/* Background con efecto parallax multi-capa */}
            <motion.div 
              className="absolute inset-0"
              initial={{ scale: 1.2 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 1.2, ease: [0.43, 0.13, 0.23, 0.96] }}
            >
              {/* Layer 1 - Imagen de fondo */}
              <motion.div
                className="absolute inset-0"
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -100, opacity: 0 }}
                transition={{ duration: 0.8, ease: "easeInOut" }}
              >
                <img
                  src={slides[currentSlide].image}
                  alt={slides[currentSlide].title}
                  className="w-full h-full object-cover"
                  style={{ objectPosition: 'center' }}
                />
              </motion.div>

              {/* Overlay gradiente dinámico */}
              <motion.div 
                className="absolute inset-0"
                style={{
                  background: `radial-gradient(circle at 30% 50%, ${slides[currentSlide].color}15 0%, transparent 50%), 
                               linear-gradient(to right, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.7) 40%, transparent 100%)`
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
              />

              {/* Efecto de partículas flotantes - Reducidas en móvil */}
              <div className="absolute inset-0">
                {[...Array(window.innerWidth < 768 ? 15 : 30)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute rounded-full"
                    style={{
                      width: Math.random() * 4 + 1,
                      height: Math.random() * 4 + 1,
                      background: slides[currentSlide].color,
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                      boxShadow: `0 0 ${Math.random() * 20 + 10}px ${slides[currentSlide].color}`
                    }}
                    animate={{
                      y: [0, -100, 0],
                      x: [0, Math.random() * 50 - 25, 0],
                      opacity: [0, 0.7, 0],
                      scale: [0, 1, 0]
                    }}
                    transition={{
                      duration: 3 + Math.random() * 2,
                      repeat: Infinity,
                      delay: Math.random() * 2,
                      ease: "easeInOut"
                    }}
                  />
                ))}
              </div>

              {/* Glitch effect removed for smoother transitions */}
            </motion.div>

            {/* Contenido con animaciones de texto avanzadas */}
            <div className="absolute inset-0 flex items-center">
              <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
                <div className="max-w-3xl">
                  {/* Badge de marca con efecto glow */}
                  <motion.div
                    initial={{ opacity: 0, y: 30, scale: 0.8 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -30, scale: 0.8 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="inline-flex items-center gap-2 md:gap-3 px-3 md:px-5 py-2 md:py-2.5 rounded-full mb-4 md:mb-8 relative overflow-hidden"
                    style={{
                      background: `linear-gradient(135deg, ${slides[currentSlide].color}20 0%, ${slides[currentSlide].color}10 100%)`,
                      backdropFilter: 'blur(20px)',
                      border: `1px solid ${slides[currentSlide].color}30`
                    }}
                  >
                    <motion.div
                      className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full"
                      style={{ backgroundColor: slides[currentSlide].color }}
                      animate={{
                        scale: [1, 1.5, 1],
                        opacity: [1, 0.5, 1]
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                    <span className="text-xs md:text-sm font-bold text-white tracking-wider">
                      {slides[currentSlide].brand}
                    </span>
                    <Sparkles className="h-3 w-3 md:h-4 md:w-4 text-white/60" />

                    {/* Efecto glow animado */}
                    <motion.div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: `radial-gradient(circle, ${slides[currentSlide].color}40 0%, transparent 70%)`
                      }}
                      animate={{
                        scale: [1, 1.5, 1],
                        opacity: [0.5, 0, 0.5]
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                  </motion.div>

                  {/* Título con efecto split reveal */}
                  <div className="overflow-hidden mb-4 md:mb-6">
                    <motion.h1
                      className="text-4xl sm:text-5xl md:text-7xl lg:text-9xl font-black text-white leading-none tracking-tighter"
                      initial={{ y: 200, rotateX: -90 }}
                      animate={{ y: 0, rotateX: 0 }}
                      exit={{ y: -200, rotateX: 90 }}
                      transition={{ 
                        duration: 0.8, 
                        delay: 0.3,
                        type: "spring",
                        stiffness: 100
                      }}
                      style={{
                        textShadow: `0 0 80px ${slides[currentSlide].color}60,
                                     0 0 40px ${slides[currentSlide].color}40,
                                     0 20px 60px rgba(0,0,0,0.8)`
                      }}
                    >
                      {slides[currentSlide].title}
                    </motion.h1>
                  </div>

                  {/* Subtitle con mask reveal */}
                  <div className="overflow-hidden mb-3 md:mb-4">
                    <motion.p
                      className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light tracking-wide"
                      style={{ 
                        color: slides[currentSlide].color,
                        textShadow: `0 0 30px ${slides[currentSlide].color}80`
                      }}
                      initial={{ x: -200, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      exit={{ x: 200, opacity: 0 }}
                      transition={{ duration: 0.6, delay: 0.5 }}
                    >
                      {slides[currentSlide].subtitle}
                    </motion.p>
                  </div>

                  {/* Description con stagger */}
                  <motion.p
                    className="text-sm sm:text-base md:text-lg lg:text-xl text-white/70 mb-6 md:mb-10 font-light tracking-wide"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                  >
                    {slides[currentSlide].description}
                  </motion.p>

                  {/* CTAs con hover magnético */}
                  <motion.div
                    className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3 md:gap-4"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -30 }}
                    transition={{ duration: 0.5, delay: 0.9 }}
                  >
                    <motion.div
                      whileHover={{ scale: 1.05, y: -5 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-full sm:w-auto"
                    >
                      <Button 
                        size="lg"
                        className="w-full sm:w-auto px-6 md:px-8 py-4 md:py-6 text-base md:text-lg font-bold relative overflow-hidden group"
                        style={{
                          background: `linear-gradient(135deg, ${slides[currentSlide].color} 0%, ${slides[currentSlide].color}dd 100%)`,
                          boxShadow: `0 10px 40px ${slides[currentSlide].color}40`
                        }}
                      >
                        <span className="relative z-10">Explorar Ahora</span>
                        <motion.div
                          className="absolute inset-0"
                          style={{ background: 'rgba(255,255,255,0.2)' }}
                          initial={{ x: '-100%' }}
                          whileHover={{ x: '100%' }}
                          transition={{ duration: 0.5 }}
                        />
                      </Button>
                    </motion.div>

                    <motion.div
                      whileHover={{ scale: 1.05, y: -5 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-full sm:w-auto"
                    >
                      <Button
                        size="lg"
                        variant="outline"
                        className="w-full sm:w-auto px-6 md:px-8 py-4 md:py-6 text-base md:text-lg font-bold text-white backdrop-blur-xl"
                        style={{
                          borderColor: slides[currentSlide].color,
                          borderWidth: '2px'
                        }}
                      >
                        Test Drive
                      </Button>
                    </motion.div>

                    {/* Logos Amaya + Marca al lado de los CTAs - Solo en desktop */}
                    <motion.div
                      className="hidden lg:flex items-center gap-4 ml-6 opacity-60 hover:opacity-90 transition-opacity"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 0.6 }}
                      transition={{ delay: 1.1 }}
                    >
                      <img 
                        src="https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/7q1rfiej_Logo_Amaya_Blanco_para_fondo_oscuro.png"
                        alt="Amaya Motors"
                        className="h-14 w-auto"
                      />
                      {slides[currentSlide].brandLogo && (
                        <>
                          <div className="h-10 w-px bg-white/40" />
                          <img 
                            src={slides[currentSlide].brandLogo}
                            alt={slides[currentSlide].brand}
                            className={slides[currentSlide].brandLogoSize === 'small' ? 'h-7 w-auto' : 'h-10 w-auto'}
                          />
                        </>
                      )}
                    </motion.div>
                  </motion.div>

                  {/* Logos Amaya + Marca en móvil - Abajo de los botones */}
                  <motion.div
                    className="lg:hidden flex items-center gap-3 mt-6 opacity-80 hover:opacity-100 transition-opacity"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.8 }}
                    transition={{ delay: 1.1 }}
                  >
                    <img 
                      src="https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/7q1rfiej_Logo_Amaya_Blanco_para_fondo_oscuro.png"
                      alt="Amaya Motors"
                      className="h-10 w-auto"
                    />
                    {slides[currentSlide].brandLogo && (
                      <>
                        <div className="h-8 w-px bg-white/50" />
                        <img 
                          src={slides[currentSlide].brandLogo}
                          alt={slides[currentSlide].brand}
                          className={slides[currentSlide].brandLogoSize === 'small' ? 'h-6 w-auto' : 'h-8 w-auto'}
                        />
                      </>
                    )}
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Controles del slider con efecto hover */}
        <div className="absolute bottom-6 md:bottom-12 right-4 md:right-12 flex gap-2 md:gap-4 z-30">
          <motion.button
            onClick={prevSlide}
            whileHover={{ scale: 1.1, x: -5 }}
            whileTap={{ scale: 0.9 }}
            className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-white/10 backdrop-blur-2xl border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all text-lg md:text-xl"
          >
            ←
          </motion.button>
          <motion.button
            onClick={nextSlide}
            whileHover={{ scale: 1.1, x: 5 }}
            whileTap={{ scale: 0.9 }}
            className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-white/10 backdrop-blur-2xl border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all text-lg md:text-xl"
          >
            →
          </motion.button>
        </div>

        {/* Indicadores de progreso innovadores */}
        <div className="absolute bottom-6 md:bottom-12 left-1/2 -translate-x-1/2 flex gap-2 md:gap-3 z-30">
          {slides.map((slide, index) => (
            <motion.button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className="relative group"
              whileHover={{ scale: 1.2 }}
            >
              <div className={`h-0.5 md:h-1 rounded-full transition-all ${
                index === currentSlide ? 'w-10 md:w-16' : 'w-6 md:w-8'
              }`}
              style={{
                backgroundColor: index === currentSlide ? slide.color : 'rgba(255,255,255,0.3)'
              }} />
              
              {index === currentSlide && (
                <motion.div
                  className="absolute inset-0 rounded-full"
                  style={{
                    boxShadow: `0 0 20px ${slide.color}, 0 0 40px ${slide.color}80`
                  }}
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.5, 0, 0.5]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              )}
            </motion.button>
          ))}
        </div>

        {/* Scroll indicator con animación - Solo desktop */}
        <motion.div
          className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 z-20"
          animate={{ y: [0, 15, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="flex flex-col items-center gap-2 text-white/40">
            <span className="text-xs font-medium tracking-widest">SCROLL</span>
            <ChevronDown className="h-5 w-5" />
          </div>
        </motion.div>
      </section>

      {/* Sección de Marcas */}
      <section id="marcas" className="relative py-20 md:py-32 pb-32 md:pb-56 lg:pb-64 bg-background transition-colors duration-500" style={{ overflow: 'visible' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16 md:mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-6xl font-black text-foreground mb-4">
              Nuestras <span className="text-primary">Marcas</span>
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Representamos las marcas más innovadoras y confiables del mercado automotriz
            </p>
          </motion.div>

          {/* Grid de marcas - diseño premium con tooltips sofisticados */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {[
              { 
                name: 'BYD', 
                logo: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/rqt16dv8_Logo_BYD_Blanco_para_fondo_oscuro.png',
                tagline: 'Innovación eléctrica',
                color: '#00d4ff'
              },
              { 
                name: 'Peugeot', 
                logo: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/usltsakg_Logo_Peugeot_Blanco_para_fondo_oscuro.png',
                tagline: 'Elegancia francesa',
                color: '#1dd1a1'
              },
              { 
                name: 'Geely', 
                logo: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/925mkif1_Logo_Geely_Blanco_para_fondo_oscuro.png',
                tagline: 'Tecnología avanzada',
                color: '#7aa8b7'
              },
              { 
                name: 'Jetour', 
                logo: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/0zf9gbf9_Logo_Jetour_Blanco_para_fondo_oscuro.png',
                tagline: 'Aventura sin límites',
                color: '#f39c12'
              },
              { 
                name: 'JAC', 
                logo: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/hoy24lx3_Logo_Jac_Blanco_para_fondo_oscuro.png',
                tagline: 'Calidad confiable',
                color: '#e74c3c'
              },
              { 
                name: 'Karry', 
                logo: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/y1p4rxta_Logo_Karry_Blanco_para_fondo_oscuro.png',
                tagline: 'Soluciones comerciales',
                color: '#9b59b6'
              },
            ].map((brand, index) => (
              <motion.div
                key={brand.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setHoveredBrand(hoveredBrand === brand.name ? null : brand.name);
                }}
                className="cursor-pointer relative brand-tooltip-trigger"
                style={{
                  transform: hoveredBrand === brand.name ? 'scale(1.05) translateY(-5px)' : 'scale(1) translateY(0)',
                  transition: 'transform 0.3s ease'
                }}
              >
                <div 
                  className="relative p-6 md:p-8 rounded-2xl transition-all duration-300 h-full flex items-center justify-center min-h-[120px]"
                  style={{
                    background: theme === 'dark' 
                      ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                      : 'linear-gradient(135deg, rgba(248,250,252,0.8) 0%, rgba(241,245,249,0.7) 100%)',
                    border: theme === 'dark' 
                      ? '1px solid rgba(255,255,255,0.1)'
                      : '1px solid rgba(0,0,0,0.06)',
                    boxShadow: theme === 'light' 
                      ? '0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)' 
                      : 'none'
                  }}
                >
                  <img 
                    src={brand.logo}
                    alt={brand.name}
                    className="w-full h-auto max-h-10 md:max-h-12 object-contain transition-all duration-300"
                    style={{ 
                      filter: theme === 'light' ? 'invert(1) brightness(0.1) contrast(1.2)' : 'none',
                      opacity: hoveredBrand === brand.name 
                        ? '1' 
                        : (theme === 'light' ? '0.95' : '0.4')
                    }}
                  />
                  {/* Efecto glow sutil en hover */}
                  <div 
                    className="absolute inset-0 rounded-2xl transition-colors duration-300"
                    style={{
                      background: theme === 'dark' 
                        ? 'transparent'
                        : 'transparent'
                    }}
                  />
                </div>

                {/* Tooltip Ultra-Refinado - Click/Tap para abrir, FIJO hasta cerrar */}
                <AnimatePresence>
                  {hoveredBrand === brand.name && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="brand-tooltip-content absolute top-full left-1/2 mt-6"
                      style={{ 
                        pointerEvents: 'auto', 
                        zIndex: 9999,
                        transform: 'translateX(-50%)',
                        position: 'absolute'
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseEnter={(e) => e.stopPropagation()}
                      onMouseLeave={(e) => e.stopPropagation()}
                      onMouseMove={(e) => e.stopPropagation()}
                    >
                    <div className="relative">
                      {/* Flecha superior con glassmorphism */}
                      <div 
                        className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 backdrop-blur-xl border-l border-t rotate-45 shadow-lg bg-popover"
                        style={{
                          borderColor: brand.color + '40'
                        }}
                      />
                      
                      {/* Contenedor principal del tooltip */}
                      <div 
                        className="relative backdrop-blur-2xl rounded-2xl shadow-2xl min-w-[260px] overflow-hidden bg-popover border"
                        style={{
                          borderColor: theme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
                          boxShadow: theme === 'dark' 
                            ? '0 20px 60px rgba(0,0,0,0.8), 0 4px 12px rgba(0,0,0,0.5)'
                            : '0 20px 60px rgba(0,0,0,0.15), 0 4px 12px rgba(0,0,0,0.08)'
                        }}
                      >
                        
                        {/* Header con marca y tagline */}
                        <div className="relative px-5 py-4 border-b" style={{ borderColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
                          <h3 className="text-foreground font-bold text-lg mb-1">{brand.name}</h3>
                          <p className="text-sm text-muted-foreground">{brand.tagline}</p>
                        </div>
                        
                        {/* Opciones del menú */}
                        <div className="relative p-3 space-y-1">
                          <a 
                            href="#0km"
                            className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground transition-all duration-200"
                            style={{
                              background: 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              e.stopPropagation();
                              e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)';
                              e.currentTarget.style.transform = 'translateX(5px)';
                            }}
                            onMouseLeave={(e) => {
                              e.stopPropagation();
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.transform = 'translateX(0)';
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setHoveredBrand(null);
                            }}
                          >
                            <Car className="h-4 w-4 relative z-10 text-muted-foreground" />
                            <span className="text-sm font-medium relative z-10">Ver {brand.name} 0km</span>
                          </a>
                          
                          <a 
                            href="#usados"
                            className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground transition-all duration-200"
                            style={{
                              background: 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              e.stopPropagation();
                              e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)';
                              e.currentTarget.style.transform = 'translateX(5px)';
                            }}
                            onMouseLeave={(e) => {
                              e.stopPropagation();
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.transform = 'translateX(0)';
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setHoveredBrand(null);
                            }}
                          >
                            <ShoppingCart className="h-4 w-4 relative z-10 text-muted-foreground" />
                            <span className="text-sm font-medium relative z-10">Ver {brand.name} Usados</span>
                          </a>
                          
                          <div 
                            className="h-px my-2" 
                            style={{ 
                              background: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' 
                            }} 
                          />
                          
                          <a 
                            href="#service"
                            className="flex items-center gap-3 px-4 py-3 rounded-xl text-muted-foreground hover:text-foreground transition-all duration-200"
                            style={{
                              background: 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              e.stopPropagation();
                              e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)';
                              e.currentTarget.style.transform = 'translateX(5px)';
                            }}
                            onMouseLeave={(e) => {
                              e.stopPropagation();
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.transform = 'translateX(0)';
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setHoveredBrand(null);
                            }}
                          >
                            <Wrench className="h-4 w-4 relative z-10 text-muted-foreground" />
                            <span className="text-sm relative z-10">Agendar Service</span>
                          </a>
                        </div>
                        
                      </div>
                    </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sección de Recomendados */}
      <section id="recomendados" className="relative py-20 md:py-32 bg-gradient-to-b from-background via-muted/30 to-background transition-colors duration-500" style={{ zIndex: 1 }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16 md:mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-6xl font-black text-foreground mb-4">
              Vehículos <span className="text-primary">Destacados</span>
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Las mejores opciones del mercado seleccionadas para vos
            </p>
          </motion.div>

          {/* Grid de autos - diseño premium */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
            {[
              {
                name: 'BYD Seal',
                category: 'Sedán Eléctrico Premium',
                price: 'USD 45.000',
                year: '2024',
                image: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/mjxopt6i_Fondo_BYD_Seal.png',
                specs: {
                  motor: '230 kW (313 CV)',
                  autonomia: '520 km WLTP',
                  aceleracion: '0-100 km/h en 3.8s',
                  bateria: '82.5 kWh',
                  traccion: 'Tracción trasera',
                  extras: ['Carga rápida 150kW', 'Piloto automático', 'Interior premium']
                }
              },
              {
                name: 'Peugeot 208',
                category: 'Hatchback Deportivo',
                price: 'USD 28.000',
                year: '2024',
                image: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/6epg1ed8_Fondo_Peugeot208.png',
                specs: {
                  motor: '1.2 PureTech 130 CV',
                  autonomia: 'N/A',
                  aceleracion: '0-100 km/h en 8.7s',
                  bateria: 'N/A',
                  traccion: 'Delantera',
                  extras: ['i-Cockpit 3D', 'Full LED', 'Asistencias ADAS']
                }
              },
              {
                name: 'Geely Coolray',
                category: 'SUV Compacta Sport',
                price: 'USD 32.000',
                year: '2024',
                image: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/a8a9t39m_Fondo_Geely_Coolray.png',
                specs: {
                  motor: '1.5 Turbo 177 CV',
                  autonomia: 'N/A',
                  aceleracion: '0-100 km/h en 7.9s',
                  bateria: 'N/A',
                  traccion: 'Delantera',
                  extras: ['DCT 7 velocidades', 'Techo panorámico', 'Multimedia 12.3"']
                }
              },
              {
                name: 'Peugeot 408',
                category: 'Fastback Híbrido',
                price: 'USD 42.000',
                year: '2024',
                image: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/0v61ajlr_Fondo_Peugeot408.png',
                specs: {
                  motor: 'Híbrido Plug-in 225 CV',
                  autonomia: '64 km eléctrico',
                  aceleracion: '0-100 km/h en 7.3s',
                  bateria: '12.4 kWh',
                  traccion: 'Delantera',
                  extras: ['Diseño fastback', 'Carga 7.4kW', 'Night Vision']
                }
              },
              {
                name: 'BYD Tang',
                category: 'SUV 7 Plazas Eléctrico',
                price: 'USD 55.000',
                year: '2024',
                image: 'https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=800&q=80',
                specs: {
                  motor: 'Dual motor 380 kW',
                  autonomia: '500 km WLTP',
                  aceleracion: '0-100 km/h en 4.4s',
                  bateria: '86.4 kWh',
                  traccion: '4x4 eléctrico',
                  extras: ['7 asientos', 'Suspensión adaptativa', 'Techo panorámico']
                }
              },
              {
                name: 'Geely X3',
                category: 'SUV Mediana Premium',
                price: 'USD 38.000',
                year: '2024',
                image: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/ispcg3ql_Fondo_Geely_X3.png',
                specs: {
                  motor: '2.0 Turbo 238 CV',
                  autonomia: 'N/A',
                  aceleracion: '0-100 km/h en 7.8s',
                  bateria: 'N/A',
                  traccion: '4x4',
                  extras: ['Sistema AWD inteligente', 'Interior cuero Nappa', 'Bose Premium']
                }
              }
            ].map((car, index) => (
              <motion.div
                key={car.name}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                whileHover={{ y: -5 }}
                className="group cursor-pointer"
              >
                <div 
                  className="relative overflow-hidden rounded-3xl transition-all duration-500"
                  style={{
                    background: theme === 'dark'
                      ? 'linear-gradient(135deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 100%)'
                      : 'linear-gradient(135deg, rgba(248,250,252,0.9) 0%, rgba(241,245,249,0.8) 100%)',
                    border: theme === 'dark'
                      ? '1px solid rgba(255,255,255,0.1)'
                      : '1px solid rgba(0,0,0,0.08)',
                    boxShadow: theme === 'light'
                      ? '0 4px 12px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.03)'
                      : 'none'
                  }}
                >
                  {/* Layout horizontal mejorado */}
                  <div className="flex flex-col lg:flex-row lg:min-h-[450px]">
                    {/* Imagen a la izquierda */}
                    <div className="relative lg:w-1/2 h-72 lg:h-auto overflow-hidden">
                      <img
                        src={car.image}
                        alt={car.name}
                        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-transparent to-transparent" />
                      
                      {/* Badge de año */}
                      <div 
                        className="absolute top-6 left-6 px-4 py-2 rounded-full backdrop-blur-xl border text-sm font-medium"
                        style={{
                          background: theme === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.9)',
                          borderColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.2)',
                          color: theme === 'dark' ? 'white' : 'black'
                        }}
                      >
                        {car.year}
                      </div>

                      {/* Efecto glow en hover */}
                      <div className="absolute inset-0 bg-gradient-to-t from-primary/0 group-hover:from-primary/10 transition-all duration-500" />
                    </div>

                    {/* Contenido a la derecha */}
                    <div className="lg:w-1/2 p-8 lg:p-10 flex flex-col justify-between">
                      {/* Header */}
                      <div className="mb-6">
                        <div 
                          className="text-xs uppercase tracking-wider mb-2 font-medium"
                          style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)' }}
                        >
                          {car.category}
                        </div>
                        <h3 
                          className="text-3xl lg:text-4xl font-black mb-4 group-hover:text-primary transition-colors duration-300"
                          style={{ color: theme === 'dark' ? 'white' : 'black' }}
                        >
                          {car.name}
                        </h3>
                      </div>

                      {/* Specs técnicos en grid */}
                      <div className="grid grid-cols-2 gap-4 mb-6 flex-1">
                        <div className="space-y-1">
                          <div 
                            className="text-xs uppercase tracking-wider"
                            style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)' }}
                          >Motor</div>
                          <div 
                            className="text-sm font-medium"
                            style={{ color: theme === 'dark' ? 'white' : 'black' }}
                          >{car.specs.motor}</div>
                        </div>
                        <div className="space-y-1">
                          <div 
                            className="text-xs uppercase tracking-wider"
                            style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)' }}
                          >Aceleración</div>
                          <div 
                            className="text-sm font-medium"
                            style={{ color: theme === 'dark' ? 'white' : 'black' }}
                          >{car.specs.aceleracion}</div>
                        </div>
                        {car.specs.autonomia !== 'N/A' && (
                          <div className="space-y-1">
                            <div 
                              className="text-xs uppercase tracking-wider"
                              style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)' }}
                            >Autonomía</div>
                            <div 
                              className="text-sm font-medium"
                              style={{ color: theme === 'dark' ? 'white' : 'black' }}
                            >{car.specs.autonomia}</div>
                          </div>
                        )}
                        <div className="space-y-1">
                          <div 
                            className="text-xs uppercase tracking-wider"
                            style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)' }}
                          >Tracción</div>
                          <div 
                            className="text-sm font-medium"
                            style={{ color: theme === 'dark' ? 'white' : 'black' }}
                          >{car.specs.traccion}</div>
                        </div>
                      </div>

                      {/* Características destacadas */}
                      <div className="mb-6">
                        <div 
                          className="text-xs uppercase tracking-wider mb-3"
                          style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)' }}
                        >Equipamiento destacado</div>
                        <div className="flex flex-wrap gap-2">
                          {car.specs.extras.map((extra, i) => (
                            <div 
                              key={i} 
                              className="px-3 py-1.5 rounded-full border text-xs"
                              style={{
                                background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                borderColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.15)',
                                color: theme === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'
                              }}
                            >
                              {extra}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Footer con precio y CTA */}
                      <div 
                        className="pt-6 border-t"
                        style={{ borderColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.15)' }}
                      >
                        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
                          <div>
                            <div 
                              className="text-xs uppercase tracking-wider mb-2"
                              style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)' }}
                            >Desde</div>
                            <div 
                              className="text-3xl lg:text-4xl font-black"
                              style={{ color: theme === 'dark' ? 'white' : 'black' }}
                            >
                              {car.price.split(' ')[0]} <span className="text-primary">{car.price.split(' ')[1]}</span>
                            </div>
                          </div>
                          <Button 
                            className="w-full sm:w-auto backdrop-blur-xl px-8 py-6 whitespace-nowrap"
                            style={{
                              background: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                              color: theme === 'dark' ? 'white' : 'black',
                              borderWidth: '1px',
                              borderColor: theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.3)'
                            }}
                          >
                            Más información
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer placeholder */}
      <footer className="bg-black border-t border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center text-white/40">
          <p>© 2024 Amaya Motors. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingVanguard;
